import React, {ReactElement, useCallback, useMemo, useState} from 'react';
import './CaseSelect.less';
import '@/ui/IconButton/IconButton.less';
import Matter, {Disposition} from '@/entities/matter';
import util from '@/lib/util';
import Select from "@/ui/Select/Select";
import Icons from "@/ui/IconButton/Icons";
import CaseHelper from "../CaseHelper";
import {formatUniqueName} from "@/Contacts/ContactFormat";
import find from "@/store/find";
import IconButton from "@/ui/IconButton/IconButton";
import {If} from "react-conditional-components-renderer";
import {Status, uuid} from "@/@types/entity";
import type {SelectOption} from "@/@types/select";
import type {RootState} from "@/@types/store";
import {connect} from "react-redux";

function CaseSelect(props: CaseSelectProps) {
    const {allowNone = true, showIcon = true} = props;

    const [showClosed, setShowClosed] = useState<boolean>(false);

    /**
     * @param {SelectOption<string, Matter>} option
     */
    const onChange = useCallback((option: SelectOption<string, Matter> | SelectOption<string, Matter>[]) => {
        if (!props.isMulti && !option) props.onChange(null);
        else if (props.isMulti && !option) props.onChange([]);
        else {
            const result = props.isMulti && Array.isArray(option) ? option.map(o => o.data) : (option as SelectOption<string, Matter>).data;

            props.onChange(result);
        }
    }, [props]);

    const sortedMatters = useMemo(() => {
        const matters = props.unsavedEntity ? [...props.matters.values(), props.unsavedEntity] : props.matters;

        return util.sortEntityArrayByString<Matter>(matters, 'name');
    }, [props.unsavedEntity, props.matters]);

    /**
     * Format a label for display in a select box.
     * Shows client names (blue) followed by case name (neutral), with wrapping between them.
     *
     * @param {Matter} matter
     * @param {string} caseName
     * @param {string[]} clientNames
     * @param {string} [iKey] - int key to use for uniqueness
     * @return {ReactElement}
     */
    const formatLabel = useCallback((matter: Matter, caseName: string, clientNames: string[], iKey: string): ReactElement => {
        return (
            <div key={caseName + clientNames} className='case-select__label'>
                {/* Icon (if required) */}
                {showIcon && <span className='case-select__label-icon'>
                    {CaseHelper.renderDispositionIcon(matter)}
				</span>}

                {/* Client Names */}
                <span className='case-select__label-clients'>{clientNames.join(' & ')}</span>

                {/* Case Name*/}
                <span className='case-select__label-case'>
                    {caseName}
                </span>
            </div>
        );
    }, [showIcon]);

    /**
     * @param {uuid|uuid[]} [selectedMatterIds]
     * @returns {SelectOption<string, Matter>[])}
     */
    const getMatters = useCallback((selectedMatterIds?: uuid | uuid[]): SelectOption<string, Matter> | SelectOption<string, Matter>[] => {
        // If no clients were specified, show all matters
        // If clients were specified, only show matters for those clients
        const matters: Matter[] = props.clientIds?.length > 0 // Client IDs specified?
                                  ? CaseHelper.getMattersForClients(props.clientIds, true, props.unsavedEntity) // Yes - limit to matters for clients in props
                                  : props.matterIds?.length > 0 // No.  Matter IDs specified?
                                    ? props.matterIds // Yes - limit to matters in props
                                        .filter(util.distinctFilter)
                                        .map(mid => CaseHelper.getMatterFromId(mid, props.unsavedEntity))
                                    : sortedMatters; // No - get everything

        // Build matters with client names for sorting
        const mattersWithClients = matters
            .filter(util.existsFilter)
            .filter(m => props.openOnly ? m.disposition === Disposition.Open : true)
            .filter(m => showClosed ? true : props.hideClosed ? ![Disposition.Closed, Disposition.Withdrawn].includes(m.disposition) && m.status !== Status.Delete : true)
            .map(matter => ({
                matter,
                clientNames: (matter.clients || []).map(cid => formatUniqueName(find('contacts', cid))),
            }));

        // Sort by client names first, then by matter name
        mattersWithClients.sort((a, b) => {
            const clientCompare = a.clientNames.join(' & ').localeCompare(b.clientNames.join(' & '));
            if (clientCompare !== 0) return clientCompare;
            return a.matter.name.localeCompare(b.matter.name);
        });

        // Create result structure
        const results: SelectOption<string, Matter>[] = mattersWithClients
            .map(({matter, clientNames}, i) => {
                const caseName = matter.name;
                const label = formatLabel(matter, caseName, clientNames, `option_${i}`);

                return {
                    label,
                    value: matter.name + ' ' + clientNames.join(' '),
                    data: matter
                };
            });

        if (selectedMatterIds) {
            if (props.isMulti) return results.filter(v => selectedMatterIds.includes(v.data.id));
            else return results.find(v => selectedMatterIds.includes(v.data.id));
        }
        else return results;
    }, [showClosed, props.unsavedEntity, props.clientIds, props.matterIds, props.openOnly, sortedMatters, props.hideClosed, formatLabel, props.isMulti]);

    const toggleClosed = useCallback(() => {
        setShowClosed(prev => !prev);
    }, []);

    return <div className='case-select'>
        <Select clsName='case-select__selector'
                name={props.name}
                allowNone={allowNone}
                autoAdvance={props.autoAdvance}
                autoFocus={props.autoFocus}
                defaultValue={props.defaultValue ? getMatters(props.defaultValue) : undefined}
                isMulti={props.isMulti}
                disabled={props.disabled}
                options={getMatters() as SelectOption<string, Matter>[]}
                onChange={onChange}
                tabSelectsValue={true}
                usesExtraFields={true}
                placeholder={props.placeholder || 'Select a case...'}
                disablePortaling={props.disablePortaling}
                menuIsOpen={props.menuIsOpen}
                menuPlacement={props.menuPlacement}
        />
        <If test={props.hideClosed}>
            <IconButton className='case-select__icon'
                        type={Icons.CASE_CLOSED}
                        title={showClosed ? 'Hide Closed Cases' : 'Show Closed Cases'}
                        primaryColor={showClosed ? 'maroon' : 'black'}
                        secondaryColor={showClosed ? 'red' : 'gray'}
                        onClick={toggleClosed}
            />
        </If>
    </div>
}

type CaseSelectProps = Pick<RootState, 'matters'> & {
    // Should we allow empty choices? Realistically this just determines if we have a clear button
    allowNone?: boolean;

    // After a single-select value is picked, advance focus to the next form field
    autoAdvance?: boolean;

    // Pass-through: focus the field when it mounts
    autoFocus?: boolean;

    // Optionally, a client to limit matters to
    clientIds?: string[]; // UUID array

    // The default value. Array if isMulti is true
    defaultValue?: string | string[]; // UUID or array of UUIDs

    // Pass-through
    disabled?: boolean;

    // Should we disable portaling?
    disablePortaling?: boolean;

    // Hide closed cases
    hideClosed?: boolean;

    // Pass-through: keep the menu open (for a select shown inside a dropdown panel)
    menuIsOpen?: boolean;

    // Menu placement: 'auto' (default), 'bottom', or 'top'. Use 'bottom' in modals.
    menuPlacement?: 'auto' | 'bottom' | 'top';

    // The selected client
    matterIds?: string[]; // UUID array

    // Should multiple clients be selectable?
    isMulti?: boolean;

    // A name for the component, passthrough to Select
    name: string;

    // Called when the client changes.  Sends back a Contact object.
    onChange: (value: Matter | Matter[] | null) => void;

    // Only show open cases?
    openOnly?: boolean;

    // Placeholder text
    placeholder?: string;

    // Show an icon for each entry?
    showIcon?: boolean;

    // If there is an unsaved entity
    unsavedEntity?: Matter;
};

const mapStateToProps = (state: RootState) => {
    return {
        matters: state.matters
    }
}

export default connect(mapStateToProps)(CaseSelect);
